import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getStorageName } from "../../helpers/storageName/storageName.ts";
import {
  signInUser,
  signOutUser,
  signUpUser,
} from "../thunks/userAuthThunk.tsx";
import { JWTTokenType, UserSignInResponse } from "../types/userAuthTypes.tsx";

interface AuthState {
  token: JWTTokenType;
  isLoading: boolean;
  error: string | null;
  isFirstLogin: boolean | null;
  companyId: number | null;
  currentBrand: number | null;
  enable_quickstart: boolean | null;
  has_completed_onboarding: boolean | null;
}

const initialState: AuthState = {
  token: null,
  isLoading: false,
  error: null,
  isFirstLogin: null,
  companyId: null,
  currentBrand: null,
  enable_quickstart: null,
  has_completed_onboarding: null,
};
export const userAuthSlice = createSlice({
  name: "userAuth",
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem(getStorageName.accessToken, action.payload);
    },
    clearAuth: (state) => {
      state.token = null;
      state.isLoading = false;
      state.error = null;
      state.isFirstLogin = null;
      state.companyId = null;
      state.currentBrand = null;
      state.enable_quickstart = null;
      state.has_completed_onboarding = null;
      localStorage.removeItem(getStorageName.accessToken);
      localStorage.removeItem(getStorageName.refreshToken);
      localStorage.removeItem(getStorageName.currentBrand);
      localStorage.removeItem(getStorageName.currentOrganization);
    },
  },
  extraReducers: (builder) => {
    builder
      // Sign In
      .addCase(signInUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        signInUser.fulfilled,
        (state, action: PayloadAction<UserSignInResponse>) => {
          state.isLoading = false;
          state.token = action.payload.access;
          state.isFirstLogin = action.payload.is_first_login;
          state.currentBrand = action.payload.current_brand;
          state.companyId = action.payload.company_id;
          state.enable_quickstart = action.payload.enable_quickstart;
          state.has_completed_onboarding =
            action.payload.has_completed_onboarding;
        },
      )
      .addCase(signInUser.rejected, (state, action) => {
        state.isLoading = false;
        state.token = null;
        state.error = action.payload as string;
      })
      .addCase(signUpUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signUpUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(signOutUser.fulfilled, (state) => {
        userAuthSlice.caseReducers.clearAuth(state);
      });
  },
});

export const { setToken, clearAuth } = userAuthSlice.actions;
